<script lang="ts">
	import { onDestroy } from 'svelte';
	import { Icon, Text } from '@Shelly/shelly-design-system';
	import {
		createMemberSettingsSnapshot,
		createMemberStatusSnapshot,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import DeviceStateSlot from 'src/components/Lists/Devices/DeviceStateSlot.svelte';
	import { edit } from 'src/stores/ui/edit_lists';
	import { sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	import type { ThumbnailResolver } from 'src/components/Camera/helpers/encryption/types/thumbnail';
	import { createThumbnailResolver } from 'src/components/Camera/helpers/encryption/recording-image-decryption';
	import { real_id } from 'src/helpers/interface_helpers';

	import {
		getCameraCardName,
		getCameraDeviceFromCard,
		getCameraDeviceSettings,
		getCameraDeviceStatus,
		isCameraCardOffline,
		isCameraSlotUsingLiveFeed,
		normalizeCameraConfig,
		type DashCameraWidgetCardRef,
	} from 'src/components/Dashboards/dash-camera-helpers';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import type { DashCameraWidgetConfig } from 'src/components/Dashboards/dash-types';
	import DashCameraDeviceCard from './DashCameraDeviceCard.svelte';

	export let cards: DashCameraWidgetCardRef[] = [];
	export let cameraConfig: DashCameraWidgetConfig = {};

	let focusedCameraId = '';
	let previewImages: Record<string, string> = {};
	$: normalizedConfig = normalizeCameraConfig(cameraConfig);
	$: variant = normalizedConfig.variant;
	$: sortedCards = (cards || []).slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
	$: cameraDeviceIds = sortedCards.map((card) => card?.id).filter(Boolean) as string[];
	$: memberStatusStore = createMemberStatusSnapshot(cameraDeviceIds);
	$: memberSettingsStore = createMemberSettingsSnapshot(cameraDeviceIds);
	$: memberStatus = $memberStatusStore;
	$: memberSettings = $memberSettingsStore;
	$: primaryCard = sortedCards[0];
	$: currentFocusedId =
		focusedCameraId || normalizedConfig.focusedCameraId || primaryCard?.id?.toString() || '';
	$: focusCard = sortedCards.find((card) => card.id?.toString() === currentFocusedId) || primaryCard;
	$: previewCards = sortedCards.filter((card) => card.id?.toString() !== focusCard?.id?.toString());
	$: previewSlots = [previewCards[0], previewCards[1]];
	$: fullPreviewCards = sortedCards.slice(0, 4);
	$: isEditing = $edit;
	$: isTripleFocusLayout = variant === 'triple-focus';

	// One memoized resolver per card keeps the current (already decrypted) image
	// until the new one resolves, then swaps and revokes the replaced blob URL, so
	// encrypted previews don't flash/leak and unchanged sources don't re-decrypt.
	const previewResolvers: Record<string, ThumbnailResolver> = {};

	const getPreviewResolver = (key: string): ThumbnailResolver => {
		if (!previewResolvers[key]) {
			previewResolvers[key] = createThumbnailResolver((url) => {
				previewImages = { ...previewImages, [key]: url };
			});
		}
		return previewResolvers[key];
	};

	const resolvePreview = (id: string | number | undefined) => {
		if (!id) return;
		const key = id.toString();
		getPreviewResolver(key).update({ deviceId: real_id(key), previewSize: 'large' });
	};

	$: sortedCards.forEach((card) => resolvePreview(card?.id));

	onDestroy(() => Object.values(previewResolvers).forEach((resolver) => resolver.destroy()));

	$: focusUsesLive = focusCard
		? isCameraSlotUsingLiveFeed(focusCard, 'main', normalizedConfig, $devices)
		: false;

	function openCamera(card?: DashCameraWidgetCardRef, event?: Event) {
		if (isEditing || !card?.id) return;
		event?.stopPropagation();
		setSidePanelState({ type: 'device', id: card.id.toString(), open: true });
	}

	function focusCamera(card?: DashCameraWidgetCardRef, event?: Event) {
		if (isEditing || !card?.id) return;
		event?.stopPropagation();
		const nextFocusId = card.id.toString();
		if (nextFocusId === currentFocusedId) return;
		focusedCameraId = nextFocusId;
	}

	function handleCardAreaClick(
		card?: DashCameraWidgetCardRef,
		role: 'main' | 'side' = 'main',
		event?: Event,
	) {
		if (isTripleFocusLayout && role === 'side') {
			focusCamera(card, event);
			return;
		}
		openCamera(card, event);
	}
</script>

{#if variant === 'full-preview' && fullPreviewCards.length}
	<div class="camera-full-preview-stack">
		{#each fullPreviewCards as card (card.id)}
			<div class="camera-full-preview-cell">
				<DeviceStateSlot deviceId={getCameraDeviceFromCard(card, $devices)?.id} let:deviceState>
					<DashCameraDeviceCard
						value={getCameraCardName(card, $devices)}
						previewImage={previewImages[card.id?.toString()]}
						imageFit="cover"
						useLiveFeed={!isEditing &&
							isCameraSlotUsingLiveFeed(card, 'main', normalizedConfig, $devices)}
						showLiveIndicator={!isEditing &&
							isCameraSlotUsingLiveFeed(card, 'main', normalizedConfig, $devices) &&
							!isCameraCardOffline(card, $devices, memberStatus, deviceState)}
						offline={isCameraCardOffline(card, $devices, memberStatus, deviceState)}
						selected={card?.id === $sidePanelStateCombined?.id}
						editMode={isEditing}
						showFullscreen={!isEditing}
						isFocusable={!isEditing}
						arrowRight={!isEditing}
						device={getCameraDeviceFromCard(card, $devices)}
						status={getCameraDeviceStatus(card, $devices, memberStatus)}
						settings={getCameraDeviceSettings(card, $devices, memberSettings)}
						useCloudConnectionIcon
						on:click={() => openCamera(card)}
						on:arrow-click={(e) => openCamera(card, e)}
						on:fullscreen={() => openCamera(card)}
						on:handle-drag-start
						on:handle-drag-end
					/>
				</DeviceStateSlot>
			</div>
		{/each}
	</div>
{:else if isTripleFocusLayout && focusCard}
	<div class="camera-trio-widget triple-focus">
		<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
		<div
			class="triple-main"
			class:edit-static={isEditing}
			role={isEditing ? undefined : 'button'}
			tabindex={isEditing ? -1 : 0}
			on:click={(e) => handleCardAreaClick(focusCard, 'main', e)}
			on:keydown={(e) => e.key === 'Enter' && handleCardAreaClick(focusCard, 'main', e)}
		>
			<DeviceStateSlot deviceId={getCameraDeviceFromCard(focusCard, $devices)?.id} let:deviceState>
				<DashCameraDeviceCard
					value={getCameraCardName(focusCard, $devices)}
					previewImage={previewImages[focusCard.id?.toString()]}
					imageFit="cover"
					useLiveFeed={!isEditing && focusUsesLive}
					showLiveIndicator={!isEditing &&
						focusUsesLive &&
						!isCameraCardOffline(focusCard, $devices, memberStatus, deviceState)}
					offline={isCameraCardOffline(focusCard, $devices, memberStatus, deviceState)}
					selected={focusCard?.id === $sidePanelStateCombined?.id}
					editMode={isEditing}
					showFullscreen={!isEditing}
					isFocusable={!isEditing}
					arrowRight={!isEditing}
					device={getCameraDeviceFromCard(focusCard, $devices)}
					status={getCameraDeviceStatus(focusCard, $devices, memberStatus)}
					settings={getCameraDeviceSettings(focusCard, $devices, memberSettings)}
					useCloudConnectionIcon
					headerCompact
					showNameOnHover
					on:arrow-click={(e) => openCamera(focusCard, e)}
					on:fullscreen={() => openCamera(focusCard)}
				/>
			</DeviceStateSlot>
		</div>

		<div class="triple-side">
			{#each previewSlots as card, index (`slot-${index}-${card?.id || 'empty'}`)}
				{#if card}
					<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
					<div
						class="triple-preview"
						class:edit-static={isEditing}
						role={isEditing ? undefined : 'button'}
						tabindex={isEditing ? -1 : 0}
						on:click={(e) => handleCardAreaClick(card, 'side', e)}
						on:keydown={(e) => e.key === 'Enter' && handleCardAreaClick(card, 'side', e)}
					>
						<DeviceStateSlot
							deviceId={getCameraDeviceFromCard(card, $devices)?.id}
							let:deviceState
						>
							<DashCameraDeviceCard
								value={getCameraCardName(card, $devices)}
								previewImage={previewImages[card.id?.toString()]}
								imageFit="cover"
								useLiveFeed={!isEditing &&
									isCameraSlotUsingLiveFeed(card, 'side', normalizedConfig, $devices)}
								showLiveIndicator={!isEditing &&
									isCameraSlotUsingLiveFeed(
										card,
										'side',
										normalizedConfig,
										$devices,
									) &&
									!isCameraCardOffline(card, $devices, memberStatus, deviceState)}
								offline={isCameraCardOffline(card, $devices, memberStatus, deviceState)}
								selected={card?.id === $sidePanelStateCombined?.id}
								editMode={isEditing}
								showFullscreen={!isEditing}
								isFocusable={!isEditing}
								arrowRight={!isEditing}
								device={getCameraDeviceFromCard(card, $devices)}
								status={getCameraDeviceStatus(card, $devices, memberStatus)}
								settings={getCameraDeviceSettings(card, $devices, memberSettings)}
								useCloudConnectionIcon
								headerCompact
								showNameOnHover
								on:arrow-click={(e) => openCamera(card, e)}
								on:fullscreen={() => openCamera(card)}
							/>
						</DeviceStateSlot>
					</div>
				{:else}
					<div class="triple-placeholder">
						<Icon icon="fal fa-video-plus" size="m" class="text-color-muted" />
						<Text styleType="body-small" class="text-color-muted text-center">
							Add additional camera here
						</Text>
					</div>
				{/if}
			{/each}
		</div>
	</div>
{/if}

<style>
	.camera-full-preview-stack {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-s);
		width: 100%;
	}

	.camera-full-preview-cell {
		width: 100%;
		min-width: 0;
		aspect-ratio: 16 / 9;
	}

	.camera-full-preview-stack :global(.dash-camera-device-card) {
		height: 100%;
		min-height: 0;
	}

	.camera-full-preview-stack :global(.camera-tile-root) {
		width: 100%;
		height: 100%;
	}

	.camera-full-preview-stack :global(.camera-tile-widget) {
		width: 100%;
		height: 100%;
		aspect-ratio: unset;
	}

	.camera-full-preview-stack :global(.video-container-wrapper),
	.camera-full-preview-stack :global(.video-player-area),
	.camera-full-preview-stack :global(.zoom-container) {
		height: 100%;
	}

	.camera-full-preview-stack :global(.video-player-area video),
	.camera-full-preview-stack :global(.state-placeholder) {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.camera-full-preview-cell :global(.dash-camera-device-card) {
		height: 100%;
	}

	/*
	 * triple-focus: ≥1126px main left + 2 right; 640–1125px main top + 2 below; <640px same compact row.
	 */
	.camera-trio-widget.triple-focus {
		display: grid;
		grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
		gap: var(--spacing-s);
		width: 100%;
		align-items: start;
	}

	.camera-trio-widget.triple-focus .triple-side {
		grid-template-columns: 1fr;
		grid-template-rows: auto auto;
	}

	.triple-main,
	.triple-preview {
		display: block;
		width: 100%;
		min-width: 0;
		aspect-ratio: 16 / 9;
		padding: 0;
		border: 0;
		border-radius: var(--radius-big);
		background: transparent;
		overflow: hidden;
		cursor: pointer;
		text-align: left;
	}

	.triple-side {
		display: grid;
		grid-template-rows: auto auto;
		gap: var(--spacing-s);
		align-content: start;
		min-width: 0;
	}

	.triple-placeholder {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: var(--spacing-xs);
		min-height: 0;
		padding: var(--spacing-s);
		border: 1px dashed var(--color-border);
		border-radius: var(--radius-big);
		background: var(--color-0);
	}

	.triple-main :global(.dash-camera-device-card),
	.triple-preview :global(.dash-camera-device-card),
	.camera-preview-button :global(.dash-camera-device-card),
	.camera-full-preview-stack :global(.camera-tile-root) {
		height: 100%;
	}

	.triple-main :global(.camera-tile-root),
	.triple-preview :global(.camera-tile-root) {
		width: 100%;
		height: 100%;
	}

	.triple-main :global(.video-container-wrapper),
	.triple-main :global(.video-player-area),
	.triple-main :global(.zoom-container),
	.triple-preview :global(.video-container-wrapper),
	.triple-preview :global(.video-player-area),
	.triple-preview :global(.zoom-container) {
		height: 100%;
	}

	.triple-main :global(.video-player-area video),
	.triple-main :global(.state-placeholder),
	.triple-preview :global(.video-player-area video),
	.triple-preview :global(.state-placeholder) {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.triple-preview :global(.camera-tile-widget),
	.triple-main :global(.camera-tile-widget) {
		width: 100%;
		height: 100%;
		aspect-ratio: unset;
	}

	.triple-placeholder {
		aspect-ratio: 16 / 9;
	}

	.triple-main.edit-static,
	.triple-preview.edit-static {
		pointer-events: none;
		cursor: default;
	}

	/* 640px–1125px: secondary cameras below main (two in a row). */
	@media (min-width: 640px) and (max-width: 1125px) {
		.camera-trio-widget.triple-focus {
			grid-template-columns: 1fr;
		}

		.camera-trio-widget.triple-focus .triple-side {
			grid-template-columns: repeat(2, minmax(0, 1fr));
			grid-template-rows: auto;
		}
	}

	@media (max-width: 639px) {
		.camera-trio-widget.triple-focus {
			grid-template-columns: 1fr;
		}

		.camera-trio-widget.triple-focus .triple-side {
			grid-template-columns: repeat(2, minmax(0, 1fr));
			grid-template-rows: auto;
		}
	}
</style>
